import { theme as coreTheme, CoreTheme } from '@kizi/core-components';
import inputs from './variants/inputs';
import buttons from './variants/buttons';
import divs from './variants/divs';
import headings from './variants/headings';
import lists from './variants/lists';
import paragraphs from './variants/paragraphs';
import svgs from './variants/svgs';
import spans from './variants/spans';
import selects from './variants/selects';
import labels from './variants/labels';
import tables from './variants/tables';
import anchors from './variants/anchors';
import tableRows from './variants/tableRows';
import tableCells from './variants/tableCells';

//this is just to show you how to add variant groups
export type ComparatorVariantGroups = 'steps' | 'timeline';

type ComparatorTheme = CoreTheme<string>;

export const baseSize = 16;

const baseColors = {
  main: ['#FFFFFF', '#004DF5', '#05164C'],
  second: ['#9B9B9B', '#D8D8D8', '#F2F2F0'],
  supplementary: [
    '#FF806E',
    '#FFA617',
    '#00DD83',
    '#B061F7',
    '#00D1DB',
    '#ED0164',
    '#C4C4C4',
  ],
  dark: ['#540021', '#492300', '#00443F', '#4F055E', '#05164C', '#5C0020'],
};

//this is just an example to show you how to copy all the keys in coreTheme.colors and redefine some keys
const colors = {
  ...baseColors,
  additionalInfo: baseColors.second[0],
  advice: baseColors.supplementary[1],
  alert: baseColors.supplementary[5],
  background: baseColors.main[0],
  danger: baseColors.supplementary[5],
  disabled: baseColors.second[0],
  disabledLight: baseColors.second[1],
  divider: baseColors.second[1],
  error: baseColors.supplementary[5],
  heading: baseColors.main[2],
  info: baseColors.main[1],
  placeholder: baseColors.second[0],
  primary: baseColors.main[1],
  rating: baseColors.main[2],
  secondary: baseColors.supplementary[4],
  success: baseColors.supplementary[2],
  tertiary: '',
  text: baseColors.main[2],
  textAdvice: baseColors.dark[1],
  warning: baseColors.supplementary[1],
  white: baseColors.main[0],
  lightMain1: `${baseColors.main[1]}4D`,
};

export const breakpoints = ['425px', '768px', '1280px', '1440px', '2000px'];

export const fonts = {
  body: '"Segoe UI", sans-serif',
  button: 'SharpSans, Helvetica, Arial, sans-serif',
  heading: 'SharpSans, Helvetica, Arial, sans-serif',
};

export const fontSizes = [11, 12, 14, 16, 18, 20, 36, 56].map(
  (px) => `${px ? px / baseSize : 0}rem`
);

export const fontWeights = [400, 700];

export const lineHeights = ['1.2', '1.4', '1.5'];

export const mediaQueries = {};

export const radii = ['0px', '4px', '10px', '50px'];

export const shadows = {
  default: `0px 0px 20px rgba(0, 0, 0, 0.15);`,
};

export const space = [0, 5, 10, 15, 20, 25, 30, 50].map(
  (px) => `${px ? px / baseSize : 0}rem`
);

export const zIndices = [0, 1, 2];

const themeCbSample: ComparatorTheme = {
  ...coreTheme,
  breakpoints,
  colors,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  radii,
  shadows,
  space,
  variants: {
    anchors,
    buttons,
    divs,
    tables,
    tableRows,
    tableCells,
    headings,
    paragraphs,
    labels,
    lists,
    selects,
    svgs,
    inputs,
    spans,
    steps: {
      active: {
        bg: 'main.2',
        border: '1px solid main.2',
        borderRadius: 3,
        height: [20, 30],
        width: [20, 30],
      },
      activeText: {
        color: 'text',
        fontFamily: 'body',
        fontSize: [1, 3],
        lineHeight: 2,
        ml: [2, 3],
      },
      dash: {
        bg: 'second.1',
        height: '1px',
        mx: [1, 3],
        width: [15, 30],
      },
      inactive: {
        bg: 'second.0',
        border: '1px solid second.1',
        borderRadius: 3,
        height: [20, 30],
        width: [20, 30],
      },
      inactiveText: {
        color: 'second.0',
        fontFamily: 'body',
        fontSize: [1, 3],
        lineHeight: 2,
        ml: [2, 3],
      },
      stepNumber: {
        color: 'white',
        fontFamily: ['body', 'heading'],
        fontSize: [1, 3],
        fontWeight: 1,
        lineHeight: 2,
      },
      verticalDash: {
        bg: 'second.1',
        height: [15, 30],
        my: [1, 3],
        ml: [2, 3],
        width: '1px',
      },
    },
    timeline: {
      dash: {
        bg: 'second.1',
        height: '100%',
        ml: 3,
        width: '1px',
      },
      dashWide: {
        bg: 'second.2',
        height: '100%',
        ml: '17px',
        width: '4px',
      },
      dashHorizontal: {
        position: 'absolute',
        top: 24,
        left: 4,
        bg: 'second.2',
        height: '2px',
        width: '90%',
      },
      item: {
        '&:not(:last-of-type)': {
          pb: 6,
        },
      },
      itemHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      },
      marker: {
        bg: 'main.2',
        border: '1px solid main.2',
        borderRadius: 3,
        height: 30,
        width: 30,
      },

      markerNumber: {
        color: 'white',
        fontFamily: 'body',
        fontSize: 2,
        fontWeight: 1,
        lineHeight: 2,
      },
      textBlock: {
        ml: 3,
        '> p:first-of-type': {
          mt: 1,
        },
      },
      textBlockHorizontal: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '>p:first-of-type': {
          fontSize: 4,
        },
        '>p:last-of-type': {
          fontSize: 1,
        },
      },
    },
  },
  global: {
    body: {
      fontFamily: fonts.body,
    },
  },
  zIndices,
};

export default themeCbSample;
